import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, Checkbox, FormControlLabel, AccordionSummary, AccordionDetails } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { GenderSelector } from '../../selectors/GenderSelector';
import { OtherContactDetailsPanel } from './OtherContactDetailsPanel';
import { ClientPostalAddressPanel } from './ClientPostalAddressPanel';
import { ClientAddressPanel } from './ClientAddressPanel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { IndividualNameSummary } from '../../selectors/IndividualSelector';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Address, PlaceType } from '../../selectors/AddressSelector';
import { addEllipsis, arrayToString, arrayToStringWithSeparator } from '../../../helpers/commonHelper';
import { TranslationTextList } from '../../../locales/common';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherDetailsPanel');

// Explicit request (EnquiryFormDrawer split): the "Client Details" accordion (name, contact details, addresses, consents).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherDetailsPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    isOtherContactDetailsPanelExpanded: boolean;
    isPostalAddressPanelExpanded: boolean;
    homeCountry: string;
    onDropDownSelect: (form: FormApi<Enquiry>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    onGetPartyAddresses: (form: FormApi<Enquiry>, values: AnyObject, selection: IndividualNameSummary, name: string) => void;
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    translate: (key: keyof TranslationTextList) => string;
    onContactDetailsChanged: (form: FormApi<any>, values: AnyObject) => void;
    onOtherContactDetailsPanelExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    birthDateValidator: FieldValidator<any>;
    getAddressLabelForPanel: (buildingName: string, level: string, streetNumber: string, street1: string, street2: string, suburb: string, addressState: string, postCode: string, country: string) => string;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    isPoBoxRequired: (values: AnyObject) => boolean;
    requiredValidator: FieldValidator<any>;
    onPostalAddressPanelExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    isSourceTypeClientOnly: true | undefined;
}

interface OtherDetailsPanelBodyProps extends OtherDetailsPanelProps {
    formValues: AnyObject;
}

const OtherDetailsPanelBody: React.FC<OtherDetailsPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, isOtherContactDetailsPanelExpanded, isPostalAddressPanelExpanded, homeCountry, onDropDownSelect, onGetPartyAddresses, onSwitch, translate, onContactDetailsChanged, onOtherContactDetailsPanelExpandChange, birthDateValidator, getAddressLabelForPanel, createAddressSelectionHandler, createInputChangedHandler, isPoBoxRequired, requiredValidator, onPostalAddressPanelExpandChange, onExpandChange, isSourceTypeClientOnly,
        formValues,
    } = bodyProps;
    const values = formValues;

    const panelTitle = addEllipsis(arrayToString([
        arrayToStringWithSeparator([
            values.clientTitle && values.clientTitle.trim().length > 0 && values.clientTitle !== 'None' ? `${values.clientTitle}` : '',
            values.clientFirstName,
            values.clientLastName,
        ], ' '),
        values.clientRelationshipToEnquirer,
        values.addressName,
        arrayToStringWithSeparator([
            arrayToStringWithSeparator([
                values.floor,
                values.number,
            ], '/'),
            values.street,
        ], ' '),
        values.street2,
        values.suburb,
        values.state,
        values.postcode,
        values.country
        // tslint:disable-next-line: align
    ]), 70);

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-details-content"
                id="other-details-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipBlue)}>
                        <PeopleAltIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelBlue, classes.sectionLabelText)}>
                        Client Details
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        {panelTitle}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={12}>
                        <OtherContactDetailsPanel
                            form={form}
                            classes={classes}
                            isExpanded={isOtherContactDetailsPanelExpanded}
                            onDropDownSelect={onDropDownSelect}
                            onGetPartyAddresses={onGetPartyAddresses}
                            onSwitch={onSwitch}
                            translate={translate}
                            onContactDetailsChanged={onContactDetailsChanged}
                            onExpandChange={onOtherContactDetailsPanelExpandChange}
                            isSourceTypeClientOnly={isSourceTypeClientOnly}
                        />
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="dOB"
                            validate={birthDateValidator}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <KeyboardDatePicker
                                    inputVariant="outlined"
                                    size="small"
                                    {...input}
                                    value={input && input.value ? input.value : null}
                                    className={classes.textField}
                                    label="Birthdate (of Client)"
                                    format="DD/MM/YYYY"
                                    placeholder="dd/mm/yyyy"
                                    animateYearScrolling={true}
                                    // tslint:disable-next-line: max-line-length
                                    autoOk={true}
                                    allowKeyboardControl={false}
                                    variant={'inline'}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error}
                                    inputProps={{ autoComplete: 'off' }}
                                    InputLabelProps={{ shrink: true }}
                                    maxDate={new Date()}
                                    onChange={(date: MaterialUiPickersDate | null, value?: string | null) => input.onChange(date)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            // tslint:disable-next-line: max-line-length
                            name="gender"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <GenderSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Gender (of Client)"
                                        name="gender"
                                        disabled={values.nameGuid}
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) =>
                                            input.onChange(selection.value)
                                        }
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? 'Gender is required'
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Grid container={true} spacing={1}>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isMarketingConsent"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isMarketingConsent}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            disabled={!!values.individualSelector}
                                            label="Marketing Consent"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isTermsAndConditionsApproved"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isTermsAndConditionsApproved}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="T&C's Approved"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isPrivacyStatementProvided"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isPrivacyStatementProvided}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Privacy Statement Provided"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isConflictCheckPerformed"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isConflictCheckPerformed}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Conflict Check Performed"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isNoConflict"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isNoConflict}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="No Conflict"
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={12} md={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                                <Field
                                    name="isIdentityCheckPerformed"
                                    type="checkbox"
                                    subscription={{ touched: true, error: true, value: true }}
                                >
                                    {({ input, meta }) => (
                                        <FormControlLabel
                                            className={classes.formControlLabel}
                                            control={
                                                <Checkbox
                                                    {...input}
                                                    checked={values.isIdentityCheckPerformed}
                                                    onChange={input.onChange}
                                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                                />
                                            }
                                            label="Identity Check Peformed"
                                        />
                                    )}
                                </Field>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item={true} xs={12}>
                        <ClientAddressPanel
                            form={form}
                            classes={classes}
                            defaultExpanded={true}
                            homeCountry={homeCountry}
                            getAddressLabelForPanel={getAddressLabelForPanel}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                        />
                    </Grid>
                    <Grid item={true} xs={12}>
                        <ClientPostalAddressPanel
                            form={form}
                            classes={classes}
                            isExpanded={isPostalAddressPanelExpanded}
                            homeCountry={homeCountry}
                            isPoBoxRequired={isPoBoxRequired}
                            requiredValidator={requiredValidator}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                            onExpandChange={onPostalAddressPanelExpandChange}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherDetailsPanelBody.displayName = 'OtherDetailsPanelBody';

export const OtherDetailsPanel: React.FC<OtherDetailsPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherDetailsPanelBody
                {...panelProps}
                formValues={{
                    clientTitle: values.clientTitle,
                    clientFirstName: values.clientFirstName,
                    clientLastName: values.clientLastName,
                    clientRelationshipToEnquirer: values.clientRelationshipToEnquirer,
                    addressName: values.addressName,
                    floor: values.floor,
                    number: values.number,
                    street: values.street,
                    street2: values.street2,
                    suburb: values.suburb,
                    state: values.state,
                    postcode: values.postcode,
                    country: values.country,
                    nameGuid: values.nameGuid,
                    isMarketingConsent: values.isMarketingConsent,
                    individualSelector: values.individualSelector,
                    isTermsAndConditionsApproved: values.isTermsAndConditionsApproved,
                    isPrivacyStatementProvided: values.isPrivacyStatementProvided,
                    isConflictCheckPerformed: values.isConflictCheckPerformed,
                    isNoConflict: values.isNoConflict,
                    isIdentityCheckPerformed: values.isIdentityCheckPerformed,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherDetailsPanel.displayName = 'OtherDetailsPanel';
